import * as _ from 'lodash';
import * as moment from 'moment';
import { Alert, Button, Glyphicon, Label, Panel, Table } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { IWatrLineItem } from '@nkr/models/watr-request-travels';
import { getMileageRateForDate } from '../year-rates';
import { getTripPhaseName } from '../verify/constants/trip-phases';

interface IProps {
    request: any;
    onViewFilesForTrip: (trip: any, lineItems: IWatrLineItem[]) => void | Promise<void>;
}

const formatAmount = (value: number | undefined) =>
    value == undefined ? 'N/A' : '$' + value.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getStatusLabel = (status: WageAndTravelReimbursementRequestStatus) => {
    if (status === WageAndTravelReimbursementRequestStatus.Reimbursed) {
        return <Label bsStyle='success' style={{ fontSize: '100%' }}>Reimbursed</Label>;
    }
    if (
        status === WageAndTravelReimbursementRequestStatus.AdminApproved
        || status === WageAndTravelReimbursementRequestStatus.CenterApproved
        || status === WageAndTravelReimbursementRequestStatus.AutoApproved
    ) {
        return <Label bsStyle='warning' style={{ fontSize: '100%' }}>Approved</Label>;
    }
    if (
        status === WageAndTravelReimbursementRequestStatus.AdminDeclined
        || status === WageAndTravelReimbursementRequestStatus.CenterDeclined
    ) {
        return <Label bsStyle='danger' style={{ fontSize: '100%' }}>Declined</Label>;
    }
    if (status === WageAndTravelReimbursementRequestStatus.PendingFix) {
        return <Label bsStyle='danger' style={{ fontSize: '100%' }}>Pending Fix</Label>;
    }
    if (status === WageAndTravelReimbursementRequestStatus.PendingApproval) {
        return <Label bsStyle='primary' style={{ fontSize: '100%' }}>Pending Approval</Label>;
    }
    return null;
};

const TravelExpenseReimbursementPanel = ({ request, onViewFilesForTrip }: IProps) => {
    const travels = request.requestTravels;

    return (
        <Panel eventKey='3'>
            <Panel.Heading style={{ paddingTop: 5, paddingBottom: 5 }}>
                <Panel.Title toggle style={{ fontSize: 14 }}>Travel Expense Reimbursement</Panel.Title>
            </Panel.Heading>
            <Panel.Body collapsible>
                {travels.length == 0 ? 'No entries recorded.'
                    :
                    travels.map((x, travelIndex) => {
                        let totalTripRequested = x.travelReceiptsTotal;
                        let trips = x.trips || [];
                        const rateForDate = getMileageRateForDate(x.createdDateTime);
                        let reimbursementRate: string;
                        if (trips.length > 0) {
                            const orderedByReimbursements = _.orderBy(trips, trip => trip.tripDate ? moment(trip.tripDate) : moment(0), ['desc']);
                            reimbursementRate = (orderedByReimbursements[0].reimbursementRate ?? rateForDate).toFixed(2);
                        } else {
                            reimbursementRate = rateForDate.toFixed(2);
                        }

                        const sortedTrips = trips.length > 0 ? _.orderBy(trips, trip => trip.tripDate ? moment(trip.tripDate) : moment(0), ['asc']) : [];
                        const allLineItems = x.lineItems || [];
                        const hasLineItemsWithFiles = allLineItems.some((li: IWatrLineItem) => li.receiptFileId) || sortedTrips.some((trip: any) => (trip.lineItems || []).some((li: IWatrLineItem) => li.receiptFileId));

                        const isLiver = request.donationOrganType === 1;
                        const limit = request.limit || {} as any;

                        const calculateLodgingReimbursable = () => {
                            if (x.hotelLodgingExpense == undefined) return undefined;
                            if ((x as any).isHotelLodgingExpenseOverridden && (x as any).overrideHotelLodgingExpense > 0) {
                                return (x as any).overrideHotelLodgingExpense;
                            }
                            const lodgingLineItems = allLineItems.filter((li: any) => li.numberOfNights != null && li.numberOfNights > 0);
                            const totalNights = lodgingLineItems.reduce((sum: number, li: any) => sum + (li.numberOfNights || 0), 0);
                            const maxNights = limit.hotelNights || (isLiver ? 30 : 14);
                            if (totalNights > maxNights && totalNights > 0) {
                                return Math.round((x.hotelLodgingExpense / totalNights) * maxNights * 100) / 100;
                            }
                            return x.hotelLodgingExpense;
                        };

                        const calculateTransportationReimbursable = () => {
                            if (x.carServiceExpense == undefined) return undefined;
                            if (x.isCarServiceExpenseOverridden && x.overrideCarServiceExpense > 0) {
                                return x.overrideCarServiceExpense;
                            }
                            const cap = limit.transportationExpenses || 700;
                            return Math.min(x.carServiceExpense, cap);
                        };

                        const calculateDependentCareReimbursable = () => {
                            if (x.dependentCareExpense == undefined) return undefined;
                            if (x.isDependentCareExpenseOverridden && x.overrideDependentCareExpense > 0) {
                                return x.overrideDependentCareExpense;
                            }
                            const cap = limit.dependentCare || (isLiver ? 3000 : 1500);
                            return Math.min(x.dependentCareExpense, cap);
                        };

                        const lodgingReimbursable = calculateLodgingReimbursable();
                        const transportationReimbursable = calculateTransportationReimbursable();
                        const dependentCareReimbursable = calculateDependentCareReimbursable();

                        return (
                            <div key={x.id ?? travelIndex} className='well' style={{ padding: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                    <div style={{ fontWeight: 'bold' }}>
                                        Request: {Utility.getOrdinalSuffixOfDate(x.createdDateTime)}
                                    </div>

                                    <div>{getStatusLabel(x.status)}</div>

                                    <div>
                                        <span style={{ color: x.status === WageAndTravelReimbursementRequestStatus.Reimbursed ? '' : '#e60000' }}>
                                            {x.status === WageAndTravelReimbursementRequestStatus.Reimbursed
                                                ? `Reimbursed Amount: $${x.reimbursementAmount}`
                                                : `Reimbursed Previously: $${x.previousReimbursementAmount}`}
                                        </span>
                                    </div>
                                </div>

                                <Table bordered condensed striped>
                                    <thead>
                                        <tr>
                                            <th>Expense</th>
                                            <th style={{ textAlign: 'right' }}>Request</th>
                                            <th style={{ textAlign: 'right' }}>Override</th>
                                            <th style={{ textAlign: 'right' }}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ fontWeight: 500 }}>Lodging Expense:</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(x.hotelLodgingExpense)}</td>
                                            <td style={{ textAlign: 'right' }}>{(x as any).isHotelLodgingExpenseOverridden && (x as any).overrideHotelLodgingExpense > 0 ? formatAmount((x as any).overrideHotelLodgingExpense) : 'N/A'}</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(lodgingReimbursable)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 500 }}>Flight/Train Expense:</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(x.flightAndTrainExpense)}</td>
                                            <td style={{ textAlign: 'right' }}>{'N/A'}</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(x.flightAndTrainExpense)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 500 }}>Transportation Expenses:</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(x.carServiceExpense)}</td>
                                            <td style={{ textAlign: 'right' }}>{x.isCarServiceExpenseOverridden && x.overrideCarServiceExpense > 0 ? formatAmount(x.overrideCarServiceExpense) : 'N/A'}</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(transportationReimbursable)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 500 }}>Dependent Care Expense:</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(x.dependentCareExpense)}</td>
                                            <td style={{ textAlign: 'right' }}>{x.isDependentCareExpenseOverridden && x.overrideDependentCareExpense > 0 ? formatAmount(x.overrideDependentCareExpense) : 'N/A'}</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(dependentCareReimbursable)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 500 }}>Meal Expense:</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(x.mealExpense)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatAmount(x.reimbursableMeals)}</td>
                                            <td style={{ textAlign: 'right' }}>{'$' + (x.reimbursableMeals || 0).toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontWeight: 500 }}>Mileage:</td>
                                            <td style={{ textAlign: 'right' }}>{x.roundTripMiles == undefined ? 0 : x.totalMileageRequested}/mi @{reimbursementRate}</td>
                                            <td style={{ textAlign: 'right' }}>{x.reimbursableMiles == undefined ? 'N/A' : '$' + x.reimbursableMiles}/mi @{reimbursementRate}</td>
                                            <td style={{ textAlign: 'right' }}>{x.reimbursableMiles == undefined ? 'N/A' : '$' + (x.reimbursableMiles * parseFloat(reimbursementRate)).toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td></td>
                                            <td></td>
                                            <td style={{ fontWeight: 500, textAlign: 'right' }}>Total:</td>
                                            <td style={{ textAlign: 'right' }}>${totalTripRequested.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        </tr>
                                    </tfoot>
                                </Table>

                                <div style={{ marginBottom: 10 }}>
                                    <table>
                                        <tbody>
                                            {x.status !== WageAndTravelReimbursementRequestStatus.Reimbursed &&
                                                <tr>
                                                    <td style={{ paddingRight: 20 }}><span style={{ color: '#005ce6' }}>Remaining Reimbursable Amount:</span></td>
                                                    <td>${x.remainingReimbursementAmount.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                </tr>
                                            }
                                            {x.status !== WageAndTravelReimbursementRequestStatus.Reimbursed &&
                                                <tr>
                                                    <td style={{ paddingRight: 20 }}><span>Allowed Reimbursable Amount:</span></td>
                                                    <td>${x.allowedReimbursementAmount.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                </tr>
                                            }
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div>
                                        {!hasLineItemsWithFiles &&
                                            <Alert bsStyle='warning' style={{ textAlign: 'center' }}>
                                                <strong>Receipts Pending</strong>
                                            </Alert>
                                        }
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                {travels.length > 0 && travels.map((x, travelIndex) => {
                    const trips = x.trips || [];
                    const sortedTrips = trips.length > 0 ? _.orderBy(trips, trip => trip.tripDate ? moment(trip.tripDate) : moment(0), ['asc']) : [];

                    if (sortedTrips.length === 0) {
                        return null;
                    }

                    let totalTripMiles = 0;
                    const allLineItems = x.lineItems || [];
                    const calculatedTotalTripReimburse = sortedTrips.reduce((sum, trip) => {
                        const tripLineItems = (trip as any).lineItems || allLineItems.filter((li: IWatrLineItem) => li.tripId === trip.id);
                        return sum + (tripLineItems.reduce((itemSum: number, li: IWatrLineItem) => itemSum + (li.amount || 0), 0));
                    }, 0);

                    return (
                        <div key={x.id ?? travelIndex} className='well' style={{ padding: 10, marginTop: 10 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 10 }}>
                                Trip Expenses
                            </div>

                            <Table bordered condensed striped>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Reason</th>
                                        <th>Miles</th>
                                        <th style={{ textAlign: 'right' }}>Amount</th>
                                        <th>Files</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTrips.map((trip, index) => {
                                        totalTripMiles += trip.tripMiles ?? 0;
                                        const tripLineItems = (trip as any).lineItems || allLineItems.filter((li: IWatrLineItem) => li.tripId === trip.id);
                                        const tripAmount = tripLineItems.reduce((sum: number, li: IWatrLineItem) => sum + (li.amount || 0), 0);
                                        const hasFiles = tripLineItems.some((li: IWatrLineItem) => li.receiptFileId);

                                        return (
                                            <tr key={trip.id || index}>
                                                <td>{trip.tripDate ? moment(trip.tripDate).format('MM/DD/YY') : '-'}</td>
                                                <td>{getTripPhaseName(trip)}</td>
                                                <td>{(trip.tripMiles ?? 0) === 0 ? '-' : trip.tripMiles}</td>
                                                <td style={{ textAlign: 'right' }}>${tripAmount.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                <td>
                                                    {hasFiles ? (
                                                        <Button
                                                            bsSize='xs'
                                                            bsStyle='link'
                                                            onClick={() => onViewFilesForTrip(trip, tripLineItems)}
                                                            style={{ padding: 0 }}
                                                        >
                                                            <Glyphicon glyph='file' /> View Files
                                                        </Button>
                                                    ) : (
                                                        <span>-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={2} style={{ textAlign: 'right' }}>Total:</td>
                                        <td>{totalTripMiles === 0 ? '-' : totalTripMiles}</td>
                                        <td style={{ textAlign: 'right' }}>${calculatedTotalTripReimburse.toLocaleString('en-us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    );
                })}
            </Panel.Body>
        </Panel>
    );
};

export default TravelExpenseReimbursementPanel;
